import React, { useState } from "react";
import { FullScreenDialog } from "../../core/FullScreenDialog";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { DateTime } from "luxon";
import { usePublisherAndContacts, useAddPublisherGameUpdateV } from "@seabrookstudios/pitch2table";
import { SmartTextField } from "../../core/SmartTextField";
import { PublisherUpdateFromSelect } from "./PublisherUpdateFromSelect";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers";
import { formatFullDate } from "../../core/date-formatting";
import { FollowUpStatusToDaysDefaults, formatPublisherStatus } from "@seabrookstudios/pitch2table-core";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").UserId} props.userId
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.publisherGame
 */
export const AddPublisherUpdate = ({ userId, publisherGame }) => {
  const { data: publisherAndContacts } = usePublisherAndContacts(publisherGame.publisherId);

  const updatePublisherGame = useAddPublisherGameUpdateV(publisherGame.id);

  const contacts = publisherAndContacts ? publisherAndContacts[1] : [];

  // the one we use for the date controls
  const [date, setDate] = useState(DateTime.now());
  // the one we send to the server
  const [newDate, setNewDate] = useState(DateTime.now().toMillis());
  const [updateText, setUpdateText] = useState("");
  const [from, setFrom] =
    /** @type {[(import('@seabrookstudios/types').UserId|'publisher'),import('react').Dispatch<import('react').SetStateAction<import('@seabrookstudios/types').UserId|'publisher'>>]} */ (
      useState(userId)
    );

  const canUpdateFollowUp = !!FollowUpStatusToDaysDefaults[publisherGame.status];
  const [followUpDate, setFollowUpDate] = useState(canUpdateFollowUp ? DateTime.now().plus({ days: 28 }) : null);

  /**
   * @param {import('@seabrookstudios/types').UserId|'publisher'} from
   */
  const onFromSelect = (from) => {
    setFrom(from);
  };

  const saveUpdate = () => {
    /**
     * @type {import("@seabrookstudios/pitch2table-core").AddPublisherGameUpdateRequest}
     */
    const request = {
      status: null,
      update: updateText,
      sharedWithDesigner: true,
      sharedWithPublisher: true,
      from,
      timestamp: newDate,
      followUp: false, // We're setting explicit date, not using auto-bump
      followUpDate: followUpDate ? followUpDate.toMillis() : undefined,
    };

    updatePublisherGame.mutate(request);
  };

  const onUpdateDate = (componentDate) => {
    setNewDate(componentDate.toMillis());
    setDate(componentDate);
  };

  const canSave = updateText.length > 0;

  return (
    <FullScreenDialog
      fullscreen={false}
      title="Add Publisher Update"
      actionText="SAVE"
      canSave={!canSave}
      onSaveHandler={saveUpdate}
      isBusy={updatePublisherGame.isPending}
    >
      <Card elevation={2}>
        <Box p={1}>
          <Stack direction="row">
            <DatePicker defaultValue={date} label="Date" onChange={onUpdateDate} />
            <Box justifyContent="center" alignItems="center" display="flex" flex={1}>
              <Typography>{formatFullDate(newDate)}</Typography>
            </Box>
          </Stack>
          <PublisherUpdateFromSelect
            value={from}
            userId={userId}
            publisherContactUserIds={contacts.map((contact) => contact.id)}
            publisherGame={publisherGame}
            onChange={onFromSelect}
          />
          <SmartTextField
            initialValue={updateText}
            onChange={setUpdateText}
            onEnterDisabled
            multiline
            minRows={5}
            autoFocus
            label="Update"
          />
          {canUpdateFollowUp ? (
            <Box mt={2}>
              <DatePicker
                label="Follow-up Date"
                value={followUpDate}
                onChange={setFollowUpDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: "Set a custom date for follow-up reminder",
                  },
                }}
              />
            </Box>
          ) : (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                No follow up is scheduled for pitches in {formatPublisherStatus(publisherGame.status)} status
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    </FullScreenDialog>
  );
};
